// Add to existing api.js exports
import axios from "axios";
import { withFrazioneScope } from "./utils/frazioneSlug";
import { adminAuthHeaders, getAdminToken, handleAdminAuthError } from "./utils/adminAuth";

// Vuoto = stesso host/porta (proxy /api in dev via src/setupProxy.js)
function normalizeBackendUrl(raw) {
  const s = (raw || "").trim().replace(/\/+$/, "");
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return `http://${s.replace(/^\/+/, "")}`;
}

const BACKEND_URL = normalizeBackendUrl(process.env.REACT_APP_BACKEND_URL);
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
/** Origin per download/upload assoluti (vuoto = stesso host del browser). */
export const BACKEND_ORIGIN =
  BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

export const api = axios.create({ baseURL: API, timeout: 60000 });
export const apiAuth = axios.create({ baseURL: API, timeout: 60000, withCredentials: true });

const ACCOUNT_TOKEN_KEY = "6amico_account_token";

function getAccountToken() {
  if (typeof localStorage === "undefined") return "";
  return localStorage.getItem(ACCOUNT_TOKEN_KEY) || "";
}

/** Header Bearer account (stessa chiave usata da /accedi). */
export const accountHeaders = () => {
  const t = getAccountToken();
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

export function isAxiosAuthError(error) {
  const status = error?.response?.status;
  return status === 401 || status === 403;
}

function getRequestAuthorization(config) {
  const h = config.headers;
  if (!h) return "";
  if (typeof h.get === "function") {
    return h.get("Authorization") || h.get("authorization") || "";
  }
  return h.Authorization || h.authorization || "";
}

function isValidBearer(value) {
  if (!value || typeof value !== "string") return false;
  if (!value.startsWith("Bearer ")) return false;
  const token = value.slice(7).trim();
  return Boolean(token) && token !== "null" && token !== "undefined";
}

function setRequestAuthorization(config, token) {
  if (!token) return;
  const value = `Bearer ${token}`;
  if (!config.headers) config.headers = {};
  if (typeof config.headers.set === "function") {
    config.headers.set("Authorization", value);
  } else {
    config.headers.Authorization = value;
  }
}

/** Stesso token del resto dell'app: admin su /admin/*, account su /account/* e sindaco. */
api.interceptors.request.use((config) => {
  const existing = getRequestAuthorization(config);
  if (existing && !isValidBearer(existing)) {
    if (!config.headers) config.headers = {};
    if (typeof config.headers.delete === "function") {
      config.headers.delete("Authorization");
      config.headers.delete("authorization");
    } else {
      delete config.headers.Authorization;
      delete config.headers.authorization;
    }
  }
  if (isValidBearer(getRequestAuthorization(config))) return config;
  const url = String(config.url || "");
  if (url.includes("/admin/")) {
    setRequestAuthorization(config, getAdminToken());
  } else if (
    url.includes("/account/") ||
    url.includes("/agora/sindaco") ||
    url.includes("/uploads/")
  ) {
    setRequestAuthorization(config, getAccountToken());
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    handleAdminAuthError(error);
    return Promise.reject(error);
  },
);

/** Timeout breve per chiamate di bootstrap (home / mappa) — evita spinner infinito. */
export const API_BOOTSTRAP_TIMEOUT_MS = 5000;
const bootstrapReq = { timeout: API_BOOTSTRAP_TIMEOUT_MS };

/** URL assolute per upload locali (/api/uploads/…). */
export function absUploadUrl(rel) {
  if (!rel) return rel;
  if (rel.startsWith("http://") || rel.startsWith("https://")) return rel;
  const origin =
    BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
  return `${origin}${rel.startsWith("/") ? rel : `/${rel}`}`;
}

apiAuth.interceptors.request.use((config) => {
  if (!isValidBearer(getRequestAuthorization(config))) {
    setRequestAuthorization(config, getAccountToken());
  }
  return config;
});

export const getVillages = (params = {}) =>
  api.get("/villages", { params }).then((r) => r.data);
export const getVillage = (slug, config) =>
  api.get(`/villages/${slug}`, config).then((r) => r.data);
export const getFrazioniOf = (slug, config) =>
  api.get(`/villages/${slug}/frazioni`, config).then((r) => r.data);
export const getAllFrazioni = () => api.get("/frazioni").then(r => r.data);
export const getFrazione = (slug) => api.get(`/frazioni/${slug}`).then(r => r.data);
export const getMonuments = (params = {}) =>
  api.get("/monuments", { params: withFrazioneScope(params) }).then((r) => r.data);
export const getParishesByFrazione = (frazione_slug) => api.get("/parishes", { params: { frazione_slug } }).then(r => r.data);

export const getPosts = (village_slug) => api.get("/posts", { params: village_slug ? { village_slug } : {} }).then(r => r.data);
export const createPost = (payload) => api.post("/posts", payload).then(r => r.data);
export const likePost = (id) => api.post(`/posts/${id}/like`).then(r => r.data);
export const getMerchants = (params = {}) =>
  api.get("/merchants", { params }).then((r) => r.data);
export const getMerchant = (id) => api.get(`/merchants/${id}`).then((r) => r.data);
export const getTwinnings = () => api.get("/twinnings").then(r => r.data);
export const getQR = (code) => api.get(`/qr/${code}`).then(r => r.data);
export const listQR = () => api.get("/qr").then(r => r.data);
export const chatSend = (payload) =>
  api.post("/chat", payload, { timeout: 90000 }).then((r) => r.data);

/**
 * Stream chat response token-per-token via SSE (Zero Attrito).
 * Calls onDelta(text) for each chunk, returns a Promise resolving to full reply.
 */
export async function chatStream(payload, onDelta) {
  const ctrl = new AbortController();
  const res = await fetch(`${API}/chat/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal: ctrl.signal,
  });
  if (!res.ok || !res.body) throw new Error(`Stream HTTP ${res.status}`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  let full = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const events = buf.split("\n\n");
    buf = events.pop() || ""; // keep tail
    for (const ev of events) {
      const line = ev.split("\n").find(l => l.startsWith("data:"));
      if (!line) continue;
      const json = line.replace(/^data:\s*/, "").trim();
      if (!json) continue;
      try {
        const data = JSON.parse(json);
        if (data.delta) { full += data.delta; onDelta?.(data.delta, full); }
        if (data.error) throw new Error(data.error);
        if (data.done) return full;
      } catch (e) { /* ignore JSON parse errors on heartbeat lines */ }
    }
  }
  return full;
}

export const qrPngVillage = (slug) => `${API}/qrcode/village/${slug}.png`;
export const qrPngFrazione = (slug) => `${API}/qrcode/frazione/${slug}.png`;
export const qrPngComune = (slug) => `${API}/qrcode/comune/${slug}.png`;

// Comuni Italia (ISTAT)
export const listComuni = (params = {}) => api.get("/comuni", { params }).then(r => r.data);
export const getComune = (slug, config) =>
  api.get(`/comuni/${slug}`, config).then((r) => r.data);
export const listRelazioniTerritorio = (params = {}) =>
  api.get("/relazioni-territorio", { params }).then((r) => r.data);
/** Payload leggero per app mobile: hero, coordinate, marker mappa, SOS. */
export const getComuneLight = (slug) => api.get(`/comuni/${slug}/light`).then((r) => r.data);
export const listRegions = () => api.get("/comuni/regions").then(r => r.data);
export const listProvinces = (region) => api.get("/comuni/provinces", { params: region ? { region } : {} }).then(r => r.data);

// Account dashboard stats
export const accountDashboardStats = () => api.get("/account/dashboard-stats", accountHeaders()).then(r => r.data);
export const accountFrazioniStats = () => api.get("/account/frazioni-stats", accountHeaders()).then(r => r.data);
export const accountEventsStats = () => api.get("/account/events-stats", accountHeaders()).then(r => r.data);

// Account password recovery
export const accountForgotPassword = (email) =>
  api.post("/account/forgot-password", { email }).then(r => r.data);
export const accountResetPassword = (token, new_password) =>
  api.post("/account/reset-password", { token, new_password }).then(r => r.data);

// Page-view tracking (fire-and-forget on detail pages)
export const trackView = (kind, slugOrId) => {
  const body = { kind };
  if (kind === "event") body.id = slugOrId;
  else body.slug = slugOrId;
  return api.post("/track-view", body).then(r => r.data).catch(() => null);
};

// Event suggestions (public → sindaco approval)
export const suggestEvent = (payload) => apiAuth.post("/event-suggestions", payload).then(r => r.data);
export const accountListEventSuggestions = (status) =>
  api.get("/account/event-suggestions", { ...accountHeaders(), params: status ? { status } : {} }).then(r => r.data);
export const accountApproveSuggestion = (id) =>
  api.post(`/account/event-suggestions/${id}/approve`, {}, accountHeaders()).then(r => r.data);
export const accountRejectSuggestion = (id, reason) =>
  api.post(`/account/event-suggestions/${id}/reject`, { reason: reason || "" }, accountHeaders()).then(r => r.data);

// Image upload (multipart, account-auth)
export const uploadImage = (file, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  const headers = accountHeaders().headers || {};
  return api.post("/uploads/image", fd, {
    headers: { ...headers, "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
    },
  }).then(r => ({ ...r.data, url: absUploadUrl(r.data.url) }));
};

// Image upload for public users (Google auth) posting on Piazza Social
export const uploadWallImage = (file, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  return apiAuth.post("/uploads/wall-image", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
    },
  }).then(r => ({ ...r.data, url: absUploadUrl(r.data.url) }));
};

// World Cities (GeoNames cities5000, population >= 100k, excluding Italy)
export const listWorldCities = (params = {}) => api.get("/world-cities", { params }).then(r => r.data);
export const getWorldCity = (slug, config) =>
  api.get(`/world-cities/${slug}`, config).then((r) => r.data);
export const listWorldCitiesMap = (params = {}) => api.get("/world-cities/map", { params }).then(r => r.data);
export const listWorldContinents = () => api.get("/world-cities/continents").then(r => r.data);
export const listWorldCountries = () => api.get("/world-cities/countries").then(r => r.data);
export const listGemellaggiMap = () => api.get("/gemellaggi-map").then(r => r.data);

// Admin
export const adminLogin = (password) => api.post("/admin/login", { password }).then(r => r.data);
const authHeaders = () => adminAuthHeaders();
export const adminUpdateVillage = (slug, payload) => api.put(`/admin/villages/${slug}`, payload, authHeaders()).then(r => r.data);
export const adminCreateVillage = (payload) => api.post("/admin/villages", payload, authHeaders()).then(r => r.data);
export const adminUpdateFrazione = (slug, payload, villageSlug = null) => {
  const params = villageSlug ? { village_slug: villageSlug } : undefined;
  return api.put(`/admin/frazioni/${slug}`, payload, { ...authHeaders(), params }).then((r) => r.data);
};
export const adminUpdateComune = (slug, payload) => api.put(`/admin/comuni/${slug}`, payload, authHeaders()).then(r => r.data);
export const adminUpdateWorldCity = (slug, payload) =>
  api.put(`/admin/world-cities/${slug}`, payload, authHeaders()).then(r => r.data);
export const adminListModulo1EuroComuni = (params = {}) =>
  api.get("/admin/comuni/modulo-1-euro", { params, ...authHeaders() }).then(r => r.data);
export const adminToggleModulo1Euro = (slug, modulo_1_euro_attivo) =>
  api.patch(`/admin/comuni/${slug}/modulo-1-euro`, { modulo_1_euro_attivo }, authHeaders()).then(r => r.data);
export const listCase1EuroComuni = (params = {}) => api.get("/case-1-euro/comuni", { params }).then(r => r.data);
export const listCase1EuroImmobili = (params = {}) => api.get("/case-1-euro/immobili", { params }).then(r => r.data);
export const registerCase1EuroInteresse = (payload) =>
  api.post("/case-1-euro/interesse", payload, { withCredentials: true }).then(r => r.data);
export const listUserNotifications = (params = {}) =>
  apiAuth.get("/user/notifications", { params }).then(r => r.data);
export const accountListCase1EuroImmobili = () =>
  api.get("/account/case-1-euro/immobili", accountHeaders()).then(r => r.data);
export const accountCreateCase1EuroImmobile = (payload) =>
  api.post("/account/case-1-euro/immobili", payload, accountHeaders()).then(r => r.data);
export const accountUpdateCase1EuroImmobile = (id, payload) =>
  api.put(`/account/case-1-euro/immobili/${id}`, payload, accountHeaders()).then(r => r.data);
export const accountDeleteCase1EuroImmobile = (id) =>
  api.delete(`/account/case-1-euro/immobili/${id}`, accountHeaders()).then(r => r.data);
export const adminCreateMonument = (payload) => api.post("/admin/monuments", payload, authHeaders()).then(r => r.data);
export const adminUpdateMonument = (id, payload) =>
  api.post(`/admin/monuments/${id}/update`, payload, authHeaders()).then(r => r.data);
export const adminDeleteMonument = (id) => api.delete(`/admin/monuments/${id}`, authHeaders()).then(r => r.data);
export const listMonumentiStorici = (params = {}) =>
  api.get("/monumenti-storici", { params: withFrazioneScope(params) }).then(r => r.data);
export const adminListMonumentiStorici = (params = {}) =>
  api.get("/admin/monumenti-storici", { params: withFrazioneScope(params), ...authHeaders() }).then(r => r.data);
export const adminCreateMonumentoStorico = (payload) => api.post("/admin/monumenti-storici", payload, authHeaders()).then(r => r.data);
export const adminUpdateMonumentoStorico = (id, payload) =>
  api.post(`/admin/monumenti-storici/${id}/update`, payload, authHeaders()).then(r => r.data);
export const adminDeleteMonumentoStorico = (id) => api.delete(`/admin/monumenti-storici/${id}`, authHeaders()).then(r => r.data);
export const adminAiFill = (payload) => api.post("/admin/ai-fill", payload, authHeaders()).then(r => r.data);

// Account requests & accounts (admin)
export const adminListRequests = (status) => api.get("/admin/account-requests", { params: status ? { status } : {}, ...authHeaders() }).then(r => r.data);
export const adminApproveRequest = (id) => api.post(`/admin/account-requests/${id}/approve`, {}, authHeaders()).then(r => r.data);
export const adminRejectRequest = (id) => api.post(`/admin/account-requests/${id}/reject`, {}, authHeaders()).then(r => r.data);
export const adminListInstitutionalProfiles = (status = "pending") =>
  api.get("/admin/profiles-institutional", { params: status ? { status } : {}, ...authHeaders() }).then(r => r.data);
export const adminApproveInstitutionalProfile = (id) =>
  api.post(`/admin/profiles-institutional/${id}/approve`, {}, authHeaders()).then(r => r.data);
export const adminRejectInstitutionalProfile = (id) =>
  api.post(`/admin/profiles-institutional/${id}/reject`, {}, authHeaders()).then(r => r.data);
export const adminListAccounts = (kind) => api.get("/admin/accounts", { params: kind ? { kind } : {}, ...authHeaders() }).then(r => r.data);
export const adminDeleteAccount = (id) => api.delete(`/admin/accounts/${id}`, authHeaders()).then(r => r.data);
export const adminResetPassword = (id) => api.post(`/admin/accounts/${id}/reset-password`, {}, authHeaders()).then(r => r.data);

// Aure Digitali
export const listAure = () => api.get("/aure").then(r => r.data);
export const adminUpsertAura = (payload) => api.post("/admin/aure", payload, authHeaders()).then(r => r.data);
export const adminDeleteAura = (id) => api.delete(`/admin/aure/${id}`, authHeaders()).then(r => r.data);

// Storie Borghi / Video Diretta (Mongo runtime; Prisma seed/CLI parallelo)
export const listStorieBorghi = (params = {}) =>
  api.get("/storie-borghi", { params }).then((r) => r.data);
export const adminInsertVideoDiretta = (payload) =>
  api.post("/admin/video-diretta", payload, authHeaders()).then((r) => r.data);
export const adminListVideoDiretta = () =>
  api.get("/admin/video-diretta", authHeaders()).then((r) => r.data);

// Filmati del territorio (Piazza Social — film/clip girati nei borghi)
export const listFilmatiTerritorio = () =>
  api.get("/filmati-territorio").then((r) => r.data);
export const adminListFilmatiTerritorio = () =>
  api.get("/admin/filmati-territorio", authHeaders()).then((r) => r.data);
export const adminUpsertFilmTerritorio = (payload) =>
  api.post("/admin/filmati-territorio", payload, authHeaders()).then((r) => r.data);
export const adminDeleteFilmTerritorio = (id) =>
  api.delete(`/admin/filmati-territorio/${id}`, authHeaders()).then((r) => r.data);

// Monument proposals
export const proposeMonument = (payload) => api.post("/monuments/propose", payload, { withCredentials: true }).then(r => r.data);
export const myProposals = () => api.get("/monuments/propose/mine", { withCredentials: true }).then(r => r.data);
export const adminListProposals = (status = "pending") => api.get("/admin/monument-proposals", { params: { status }, ...authHeaders() }).then(r => r.data);
export const adminApproveProposal = (id) => api.post(`/admin/monument-proposals/${id}/approve`, {}, authHeaders()).then(r => r.data);
export const adminRejectProposal = (id, reason) => api.post(`/admin/monument-proposals/${id}/reject`, { reason }, authHeaders()).then(r => r.data);

// Events
export const listEvents = (params = {}, config = {}) =>
  api.get("/events", { ...config, params }).then((r) => r.data);
export const getEvent = (id) => api.get(`/events/${id}`).then(r => r.data);
export const requestAccount = (payload) => api.post("/account/request", payload).then(r => r.data);

// Businesses
export const listBusinesses = (params = {}, config = {}) =>
  api.get("/businesses", { ...config, params }).then((r) => r.data);
export const getBusiness = (id) => api.get(`/businesses/${id}`).then(r => r.data);
export const listNearbyPOIs = (slug, params = {}) => api.get(`/comuni/${slug}/nearby`, { params }).then(r => r.data);

// Weekend
export const getComuneWeekend = (slug) => api.get(`/comuni/${slug}/weekend`).then(r => r.data);
export const listWeekends = (limit = 12) => api.get("/weekends", { params: { limit } }).then(r => r.data);
export const listCuratedLeaderboard = (limit = 6, days) =>
  api.get("/comuni/curated-leaderboard", { params: { limit, ...(days ? { days } : {}) } }).then(r => r.data);
export const getBorgoDellaSettimana = () =>
  api
    .get("/borgo-della-settimana", bootstrapReq)
    .then((r) => r.data)
    .catch(() => null);
export const wallGlobalStats = () => api.get("/wall-global/stats").then(r => r.data);
export const wallGlobalLeaderboard = (params = {}) =>
  api.get("/wall-global/leaderboard", { params }).then(r => r.data);
export const listWallGlobal = (params = {}) => api.get("/wall-global", { params }).then(r => r.data);
export const listNews = (params = {}) => api.get("/news", { params }).then(r => r.data);

// Gemellaggi
export const nominatimSearch = (q, limit = 8) => api.get("/nominatim/search", { params: { q, limit } }).then(r => r.data);
export const adminListGemellaggi = (params = {}) =>
  api.get("/admin/gemellaggi", { params, ...authHeaders() }).then(r => r.data);
export const adminApproveGemellaggio = (id) =>
  api.post(`/admin/gemellaggi/${id}/approve`, {}, authHeaders()).then(r => r.data);
export const adminRejectGemellaggio = (id) =>
  api.post(`/admin/gemellaggi/${id}/reject`, {}, authHeaders()).then(r => r.data);
export const listGemellaggi = (params = {}, config = {}) =>
  api.get("/gemellaggi", { ...config, params }).then((r) => r.data);
export const getGemellaggio = (id) => api.get(`/gemellaggi/${id}`).then(r => r.data);
export const accountListMyGemellaggi = () => api.get("/account/gemellaggi", accountHeaders()).then(r => r.data);
export const accountCreateGemellaggio = (payload) => api.post("/account/gemellaggi", payload, accountHeaders()).then(r => r.data);
export const accountUpdateGemellaggio = (id, payload) => api.put(`/account/gemellaggi/${id}`, payload, accountHeaders()).then(r => r.data);
export const accountDeleteGemellaggio = (id) => api.delete(`/account/gemellaggi/${id}`, accountHeaders()).then(r => r.data);
export const accountListMyEvents = () => api.get("/account/events", accountHeaders()).then(r => r.data);
export const accountCreateEvent = (payload) => api.post("/account/events", payload, accountHeaders()).then(r => r.data);
export const accountUpdateEvent = (id, payload) => api.put(`/account/events/${id}`, payload, accountHeaders()).then(r => r.data);
export const accountDeleteEvent = (id) => api.delete(`/account/events/${id}`, accountHeaders()).then(r => r.data);
export const adminListEvents = (params = {}) => api.get("/admin/events", { params, ...authHeaders() }).then(r => r.data);
export const adminListIcalFeeds = () =>
  api.get("/admin/ical-feeds", adminAuthHeaders()).then(r => r.data);
export const adminCreateIcalFeed = (payload) =>
  api.post("/admin/ical-feeds", payload, adminAuthHeaders()).then(r => r.data);
export const adminDeleteIcalFeed = (id) =>
  api.delete(`/admin/ical-feeds/${id}`, adminAuthHeaders()).then(r => r.data);
export const adminPollIcalFeed = (id) =>
  api.post(`/admin/ical-feeds/${id}/poll`, null, adminAuthHeaders()).then(r => r.data);
export const adminToggleIcalFeed = (id) =>
  api.post(`/admin/ical-feeds/${id}/toggle`, null, adminAuthHeaders()).then(r => r.data);
export const adminDeleteEvent = (id) => api.delete(`/admin/events/${id}`, authHeaders()).then(r => r.data);
export const accountLogin = (email, password) => api.post("/account/login", { email, password }).then(r => r.data);
export const accountMe = () => api.get("/account/me", accountHeaders()).then(r => r.data);
export const accountLogout = () => api.post("/account/logout", {}, accountHeaders()).then(r => r.data);
export const accountUpdateComune = (payload) => api.put("/account/comune/content", payload, accountHeaders()).then(r => r.data);
export const accountListMyFrazioni = () => api.get("/account/frazioni", accountHeaders()).then(r => r.data);
export const accountUpdateFrazione = (slug, payload) => api.put(`/account/frazioni/${slug}`, payload, accountHeaders()).then(r => r.data);
export const accountListMyMemoriaStorica = (params = {}) => api.get("/account/monumenti-storici", { params, ...accountHeaders() }).then(r => r.data);
export const accountCreateMemoriaStorica = (payload) => api.post("/account/monumenti-storici", payload, accountHeaders()).then(r => r.data);
export const accountDeleteMemoriaStorica = (id) => api.delete(`/account/monumenti-storici/${id}`, accountHeaders()).then(r => r.data);
export const accountListMyBusinesses = () => api.get("/account/businesses", accountHeaders()).then(r => r.data);
export const accountCreateBusiness = (payload) => api.post("/account/businesses", payload, accountHeaders()).then(r => r.data);
export const accountUpdateBusiness = (id, payload) => api.put(`/account/businesses/${id}`, payload, accountHeaders()).then(r => r.data);
export const accountDeleteBusiness = (id) => api.delete(`/account/businesses/${id}`, accountHeaders()).then(r => r.data);
export const adminListBusinesses = (params = {}) => api.get("/admin/businesses", { params, ...authHeaders() }).then(r => r.data);
export const adminGetBusiness = (id) => api.get(`/admin/businesses/${id}`, authHeaders()).then(r => r.data);
export const adminCreateBusiness = (payload) => api.post("/admin/businesses", payload, authHeaders()).then(r => r.data);
export const adminUpdateBusiness = (id, payload) => api.put(`/admin/businesses/${id}`, payload, authHeaders()).then(r => r.data);
export const adminDeleteBusiness = (id) => api.delete(`/admin/businesses/${id}`, authHeaders()).then(r => r.data);

// Wall posts (visitor Google auth)
export const wallList = (slug, params = {}) => api.get(`/wall/${slug}`, { params }).then(r => r.data);
export const wallMine = (params = {}) => apiAuth.get("/wall-mine", { params }).then(r => r.data);
export const updateMyProfile = (payload) => apiAuth.patch("/auth/profile", payload).then(r => r.data);
export const wallMeta = (slug) => api.get(`/wall/${slug}/meta`).then(r => r.data);
export const wallCreate = (payload) => apiAuth.post("/wall", payload).then(r => r.data);
export const wallLike = (id) => apiAuth.post(`/wall/${id}/like`).then(r => r.data);
export const wallReact = (id, kind) => apiAuth.post(`/wall/${id}/react`, { kind }).then(r => r.data);
export const wallComment = (id, text) => apiAuth.post(`/wall/${id}/comment`, { text }).then(r => r.data);
export const wallCommentDelete = (postId, commentId) => apiAuth.delete(`/wall/${postId}/comments/${commentId}`).then(r => r.data);
export const wallDelete = (id) => apiAuth.delete(`/wall/${id}`).then(r => r.data);
export const wallFlag = (id) => apiAuth.post(`/wall/${id}/flag`).then(r => r.data);
export const adminWallDelete = (id) =>
  api.delete(`/admin/wall/${id}`, adminAuthHeaders()).then(r => r.data);
/** pinned: true|false|undefined (undefined = toggle) */
export const adminWallPin = (id, pinned) =>
  api
    .post(`/admin/wall/${id}/pin`, null, {
      ...adminAuthHeaders(),
      params: pinned === undefined ? {} : { pinned },
    })
    .then(r => r.data);

// Event bookings
export const eventAvailability = (eventId) => api.get(`/events/${eventId}/availability`).then(r => r.data);
export const createEventBooking = (eventId, payload) => api.post(`/events/${eventId}/bookings`, payload).then(r => r.data);
export const accountListEventBookings = (eventId) =>
  api.get(`/account/events/${eventId}/bookings`, accountHeaders()).then(r => r.data);
export const accountCancelBooking = (bookingId) =>
  api.delete(`/account/bookings/${bookingId}`, accountHeaders()).then(r => r.data);

// Products
export const listProducts = (params = {}, config = {}) =>
  api.get("/products", { ...config, params }).then((r) => r.data);
export const productCheckoutSession = (payload) =>
  api.post("/products/checkout-session", payload).then(r => r.data);
export const getProduct = (id) => api.get(`/products/${id}`).then(r => r.data);
export const listProductCategories = () => api.get("/products/categories").then(r => r.data);
export const accountListMyProducts = () => api.get("/account/products", accountHeaders()).then(r => r.data);
export const accountCreateProduct = (payload) => api.post("/account/products", payload, accountHeaders()).then(r => r.data);
export const accountUpdateProduct = (id, payload) => api.put(`/account/products/${id}`, payload, accountHeaders()).then(r => r.data);
export const accountDeleteProduct = (id) => api.delete(`/account/products/${id}`, accountHeaders()).then(r => r.data);

// Messaging
function bothHeaders() {
  // Caller may be either visitor (cookie) or account (Bearer)
  const accTok = typeof window !== "undefined" ? localStorage.getItem("6amico_account_token") : null;
  return accTok ? { headers: { Authorization: `Bearer ${accTok}` }, withCredentials: true } : { withCredentials: true };
}
export const messageStart = (payload) => apiAuth.post("/messages/start", payload, { withCredentials: true }).then(r => r.data);
export const listConversations = () => api.get("/messages/conversations", bothHeaders()).then(r => r.data);
export const listMessages = (convId) => api.get(`/messages/${convId}`, bothHeaders()).then(r => r.data);
export const sendMessage = (convId, payload) => api.post(`/messages/${convId}`, payload, bothHeaders()).then(r => r.data);

// Segui il borgo — notifica quando qualcuno posta in quella Piazza
export const getFollowStatus = (slug) => apiAuth.get(`/follows/${slug}/status`).then(r => r.data);
export const followBorgo = (slug) => apiAuth.post(`/follows/${slug}`).then(r => r.data);
export const unfollowBorgo = (slug) => apiAuth.delete(`/follows/${slug}`).then(r => r.data);

// Capsule del Tempo
export const listCapsule = (params = {}) => api.get("/capsule", { params }).then(r => r.data);
export const listCapsuleByComune = (slug, limit = 50) => api.get(`/capsule/comune/${slug}`, { params: { limit } }).then(r => r.data);
export const getCapsule = (id) => api.get(`/capsule/${id}`).then(r => r.data);
export const createCapsule = (payload) => apiAuth.post("/capsule", payload).then(r => r.data);
export const updateCapsule = (id, payload) => apiAuth.put(`/capsule/${id}`, payload).then(r => r.data);
export const likeCapsule = (id) => apiAuth.post(`/capsule/${id}/like`).then(r => r.data);
export const flagCapsule = (id) => apiAuth.post(`/capsule/${id}/flag`).then(r => r.data);
export const deleteCapsule = (id) => apiAuth.delete(`/capsule/${id}`).then(r => r.data);
export const adminListCapsule = (params = {}) =>
  api.get("/admin/capsule", { ...adminAuthHeaders(), params }).then(r => r.data);
export const adminHideCapsule = (id) =>
  api.post(`/admin/capsule/${id}/hide`, null, adminAuthHeaders()).then(r => r.data);
export const adminUnflagCapsule = (id) =>
  api.post(`/admin/capsule/${id}/unflag`, null, adminAuthHeaders()).then(r => r.data);
export const adminDeleteCapsule = (id) =>
  api.delete(`/admin/capsule/${id}`, adminAuthHeaders()).then(r => r.data);

// GDPR / Privacy (cookie sessione visitatore o Bearer account)
export const privacyGetConsents = () =>
  apiAuth.get("/privacy/consents", bothHeaders()).then((r) => r.data);
export const privacyUpdateConsents = (payload) =>
  apiAuth.put("/privacy/consents", payload, bothHeaders()).then((r) => r.data);
export const privacyExportMyData = () =>
  apiAuth.get("/privacy/export", bothHeaders()).then((r) => r.data);
export const privacyDownloadMyData = async () => {
  const res = await apiAuth.get("/privacy/export/download", {
    ...bothHeaders(),
    responseType: "blob",
  });
  const blob = new Blob([res.data], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "6amico-miei-dati.json";
  a.click();
  URL.revokeObjectURL(url);
};
export const privacyEraseMe = (payload) =>
  apiAuth.delete("/privacy/me", { ...bothHeaders(), data: payload }).then((r) => r.data);

/** Elimina account cittadino + contenuti (GDPR Art. 17). Backend: DELETE /auth/me. */
export const deleteMyAccount = (payload) =>
  apiAuth.delete("/auth/me", { data: payload }).then((r) => r.data);

/** Imposta/cambia password cittadino. Backend: POST /auth/citizen-set-password. */
export const setCitizenPassword = (payload) =>
  apiAuth.post("/auth/citizen-set-password", payload).then((r) => r.data);

/** Catalogo Open Data pubblico (nessuna auth). */
export const getOpenDataCatalog = (params = {}) =>
  api.get("/v1/opendata", { params }).then((r) => r.data);

// Heritage / Registro innovazione (dal 2013)
export const getHeritageTimeline = (comune_slug) =>
  api.get("/heritage/timeline", { params: comune_slug ? { comune_slug } : {} }).then((r) => r.data);
export const getHeritageGoodPractices = (comune_slug) =>
  api.get("/heritage/good-practices", { params: comune_slug ? { comune_slug } : {} }).then((r) => r.data);

// Trasparenza fondi & ecosistema
export const listFundedProjects = (comune_slug) =>
  api.get("/funded-projects", { params: { comune_slug } }).then((r) => r.data);
export const getEcosystemAggregator = (comune_slug) =>
  api.get("/ecosystem/aggregator", { params: { comune_slug } }).then((r) => r.data);
export const getEmbedConfig = (comune_slug) =>
  api.get("/embed/config", { params: { comune_slug } }).then((r) => r.data);
export const getEmbedEvents = (comune_slug) =>
  api.get("/embed/events", { params: { comune_slug } }).then((r) => r.data);
export const getEmbedProducts = (comune_slug) =>
  api.get("/embed/products", { params: { comune_slug } }).then((r) => r.data);
export const getEmbedAlerts = (comune_slug) =>
  api.get("/embed/alerts", { params: { comune_slug } }).then((r) => r.data);

export const accountEcosystemDashboard = () =>
  api.get("/account/ecosystem/dashboard", accountHeaders()).then((r) => r.data);
export const accountListIntegrations = () =>
  api.get("/account/integrations", accountHeaders()).then((r) => r.data);
export const accountCreateIntegration = (payload) =>
  api.post("/account/integrations", payload, accountHeaders()).then((r) => r.data);

const adminHdr = () => adminAuthHeaders();
export const adminListInnovationTimeline = () =>
  api.get("/admin/innovation/timeline", adminHdr()).then((r) => r.data);
export const adminCreateTimeline = (payload) =>
  api.post("/admin/innovation/timeline", payload, adminHdr()).then((r) => r.data);
export const adminDeleteTimeline = (id) =>
  api.delete(`/admin/innovation/timeline/${id}`, adminHdr()).then((r) => r.data);
export const adminListInnovationPractices = () =>
  api.get("/admin/innovation/practices", adminHdr()).then((r) => r.data);
export const adminCreatePractice = (payload) =>
  api.post("/admin/innovation/practices", payload, adminHdr()).then((r) => r.data);
export const adminDeletePractice = (id) =>
  api.delete(`/admin/innovation/practices/${id}`, adminHdr()).then((r) => r.data);
export const adminListFundedProjects = (comune_slug) =>
  api.get("/admin/funded-projects", { ...adminHdr(), params: comune_slug ? { comune_slug } : {} }).then((r) => r.data);
export const adminCreateFundedProject = (payload) =>
  api.post("/admin/funded-projects", payload, adminHdr()).then((r) => r.data);
export const adminDeleteFundedProject = (id) =>
  api.delete(`/admin/funded-projects/${id}`, adminHdr()).then((r) => r.data);
export const adminListIntegrations = () =>
  api.get("/admin/integrations", adminHdr()).then((r) => r.data);
export const adminCreateIntegration = (payload) =>
  api.post("/admin/integrations", payload, adminHdr()).then((r) => r.data);

export const listCapsuleCategories = () => api.get("/capsule/categories").then(r => r.data);

/** Archivio podcast geolocalizzati (capsule, Cicerone, monumenti). */
export const listPodcastArchive = (params = {}) =>
  api.get("/podcast/archive", { params }).then((r) => r.data);

// Agorà — Sondaggi (sindaco endpoints)
export const accountListPolls = (comuneSlug) => api.get("/agora", { params: { comune_slug: comuneSlug, status: "all" } }).then(r => r.data);
export const accountCreatePoll = (payload) => api.post("/agora/sindaco/create", payload, accountHeaders()).then(r => r.data);
export const accountClosePoll = (pollId) => api.delete(`/agora/sindaco/${pollId}`, accountHeaders()).then(r => r.data);

// ============ BOOKING V5.0 (Hotel/B&B/Agriturismo) ============
// Host subscription
export const hostGetSubscription = () => apiAuth.get("/host/subscription").then(r => r.data);
export const hostStartTrial = () => apiAuth.post("/host/subscribe/start-trial").then(r => r.data);
export const hostSubscribeCheckout = (origin_url) => apiAuth.post("/host/subscribe/checkout", { origin_url }).then(r => r.data);
export const hostSubscriptionStatus = (sessionId) => api.get(`/host/subscribe/status/${sessionId}`).then(r => r.data);
// Host rooms (CRUD)
export const hostListRooms = () => apiAuth.get("/host/rooms").then(r => r.data);
export const hostCreateRoom = (payload) => apiAuth.post("/host/rooms", payload).then(r => r.data);
export const hostUpdateRoom = (id, payload) => apiAuth.put(`/host/rooms/${id}`, payload).then(r => r.data);
export const hostDeleteRoom = (id) => apiAuth.delete(`/host/rooms/${id}`).then(r => r.data);
// Public rooms
export const listRooms = (params = {}) => api.get("/rooms", { params }).then(r => r.data);
export const getRoom = (id) => api.get(`/rooms/${id}`).then(r => r.data);
export const checkRoomAvailability = (id, check_in, check_out) =>
  api.get(`/rooms/${id}/availability`, { params: { check_in, check_out } }).then(r => r.data);
// Bookings (guest)
export const createRoomBooking = (roomId, payload) => api.post(`/rooms/${roomId}/bookings`, payload).then(r => r.data);
export const getBookingStatus = (sessionId) => api.get(`/bookings/status/${sessionId}`).then(r => r.data);
export const myBookings = () => apiAuth.get("/account/bookings").then(r => r.data);
// Bookings (host)
export const hostListBookings = () => apiAuth.get("/host/bookings").then(r => r.data);
export const hostCheckinGuest = (id) => apiAuth.post(`/host/bookings/${id}/checkin`).then(r => r.data);

// Traduzione on-demand (lingua visitatore)
export const translateText = (payload) => api.post("/translate", payload).then(r => r.data);

/** Sblocco Passaporto sul posto (GPS geofence o QR Campovalano). */
export const unlockSigilloOnsite = (payload) =>
  apiAuth.post("/sigilli/unlock-onsite", payload).then((r) => r.data);

/** Manifest Campli: lat, lon, ar_tag, audio_file, overlay AR. */
export const getCampliMonumentiAr = () => api.get("/campli/monumenti-ar").then((r) => r.data);

/** Indice schede AR Civitella (Villa Lempa, Ripe, Capoluogo/Fortezza). */
export const getCivitellaArIndex = () => api.get("/civitella/ar").then((r) => r.data);
export const getCivitellaVillaLempaAr = () => api.get("/civitella/villa-lempa/ar").then((r) => r.data);
export const getCivitellaRipeAr = () => api.get("/civitella/ripe/ar").then((r) => r.data);
export const getCivitellaCapoluogoAr = () => api.get("/civitella/capoluogo/ar").then((r) => r.data);
export const getCivitellaOspitalita = () => api.get("/civitella/ospitalita").then((r) => r.data);
export const getCivitellaMonumentoAr = (poiId) =>
  api.get(`/civitella/monumenti-ar/${encodeURIComponent(poiId)}`).then((r) => r.data);

/** Nodo Zero · layer Memoria/Presente (stateless + asset Custodi approvati). */
export const listPlaceNodes = (comuneSlug) =>
  api.get(`/nodes/${comuneSlug}`).then((r) => r.data);

export const getPlaceNode = (comuneSlug, nodeSlug, { memoriaYear } = {}) =>
  api
    .get(`/nodes/${comuneSlug}/${nodeSlug}`, {
      params: memoriaYear != null ? { memoria_year: memoriaYear } : {},
    })
    .then((r) => r.data);

export const getPlaceNodeSynthesis = (comuneSlug, nodeSlug, { memoriaYear } = {}) =>
  api
    .get(`/nodes/${comuneSlug}/${nodeSlug}/synthesis`, {
      params: memoriaYear != null ? { memoria_year: memoriaYear } : {},
    })
    .then((r) => r.data);

export const submitLayerPresente = (comuneSlug, nodeSlug, payload) =>
  api.post(`/nodes/${comuneSlug}/${nodeSlug}/layer-presente`, payload).then((r) => r.data);

export const listParishes = (comune_slug) =>
  api.get("/parishes", { params: { comune_slug, limit: 200 } }).then(r => r.data.items || r.data);

export const placesSearch = (q, limit = 12) =>
  api.get("/places/search", { params: { q, limit } }).then(r => r.data);

export const placesMap = ({ min_lat, min_lng, max_lat, max_lng, kinds }) =>
  api.get("/places/map", { params: { min_lat, min_lng, max_lat, max_lng, kinds } }).then(r => r.data);

/** Comuni italiani in bbox — layer mappa nazionale (cluster). */
export const comuniMap = ({ min_lat, min_lng, max_lat, max_lng, include_all, zoom, limit }) =>
  api
    .get("/comuni/map", {
      params: { min_lat, min_lng, max_lat, max_lng, include_all, zoom, limit },
      ...bootstrapReq,
    })
    .then((r) => r.data)
    .catch(() => ({ items: [] }));

export const adminListTruncatedTexts = (village_slug) =>
  api.get("/admin/historical-texts/truncated", {
    params: village_slug ? { village_slug } : {},
    ...adminAuthHeaders(),
  }).then(r => r.data);

export const adminSaveHistoricalText = (payload) =>
  api.post("/admin/historical-texts", payload, adminAuthHeaders()).then(r => r.data);

export const adminAnalyticsSummary = (days = 30) =>
  api.get("/admin/analytics/summary", {
    params: { days },
    ...adminAuthHeaders(),
  }).then(r => r.data);

/** Breakdown sezioni/monumenti per un comune (modal Analytics). */
export const adminAnalyticsBreakdown = (comuneSlug, days = 30, limit = 25) =>
  api
    .get("/admin/analytics/breakdown", {
      params: { comune_slug: comuneSlug, days, limit },
      ...adminAuthHeaders(),
    })
    .then((r) => r.data);

// Rotta Nautica (admin manual CRUD: porti/circoli/rotte)
export const adminListRottaNauticaNodes = () =>
  api.get("/admin/rotta-nautica/nodes", authHeaders()).then(r => r.data);
export const adminCreateRottaNauticaNode = (payload) =>
  api.post("/admin/rotta-nautica/nodes", payload, authHeaders()).then(r => r.data);
export const adminUpdateRottaNauticaNode = (slug, payload) =>
  api.put(`/admin/rotta-nautica/nodes/${slug}`, payload, authHeaders()).then(r => r.data);
export const adminDeleteRottaNauticaNode = (slug) =>
  api.delete(`/admin/rotta-nautica/nodes/${slug}`, authHeaders()).then(r => r.data);
export const adminListRottaNauticaRoutes = () =>
  api.get("/admin/rotta-nautica/routes", authHeaders()).then(r => r.data);
export const adminCreateRottaNauticaRoute = (payload) =>
  api.post("/admin/rotta-nautica/routes", payload, authHeaders()).then(r => r.data);
export const adminUpdateRottaNauticaRoute = (slug, payload) =>
  api.put(`/admin/rotta-nautica/routes/${slug}`, payload, authHeaders()).then(r => r.data);
export const adminDeleteRottaNauticaRoute = (slug) =>
  api.delete(`/admin/rotta-nautica/routes/${slug}`, authHeaders()).then(r => r.data);

export const listInstitutionalPrograms = (kind, comune_slug) =>
  api.get("/institutional/programs", { params: { kind, comune_slug } }).then(r => r.data);

export const accountCreateMeeting = (payload) =>
  api.post("/account/meetings", payload, accountHeaders()).then(r => r.data);

export const accountListMeetings = () =>
  api.get("/account/meetings", accountHeaders()).then(r => r.data);

export const qrAssetUrl = (kind, entityId) =>
  `${API}/qr/${kind}/${entityId}.png`;

// Proximity Engines (beacon porti + geofence GPS)
export const getProssimitaConfig = () =>
  api.get("/prossimita/config", bootstrapReq).then((r) => r.data).catch(() => null);
export const scanProssimitaBeacon = (uuid) =>
  api.post("/prossimita/beacon", { uuid }).then((r) => r.data);

export default api;
